import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StaticValueCondition as StaticCondition, MockEntity } from './flexible-filter-types';
import { fetchEntityFields, fetchOptionSetValues } from '@/lib/api';
import { getOperatorsForFieldType } from '../shared/operators-config';
import {
  areFieldTypesCompatible,
  filterCompatibleFields,
  getIncompatibilityReason,
  getShortTypeDisplayName
} from '@/lib/utils/fieldTypeCompatibility';

interface StaticValueConditionProps {
  condition: StaticCondition;
  entities: MockEntity[];
  onChange: (condition: StaticCondition) => void;
  parentEntity?: string;
  childEntity?: string;
  filterLocation?: 'source' | 'target'; // Auto-sets entity based on filter location in pipeline
}

// Entity Search Select Component
const EntitySearchSelect: React.FC<{
  value: string;
  onValueChange: (value: string) => void;
  entities: MockEntity[];
  placeholder?: string;
}> = ({ value, onValueChange, entities, placeholder = "Select entity..." }) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const selectedEntity = entities.find(entity => entity.LogicalName === value);

  // Filter entities based on search query
  const filteredEntities = React.useMemo(() => {
    if (!searchQuery.trim()) return entities;
    
    const query = searchQuery.toLowerCase();
    return entities.filter(entity => 
      (entity.SchemaName || '').toLowerCase().includes(query) ||
      (entity.LogicalName || '').toLowerCase().includes(query)
    );
  }, [entities, searchQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-background px-3 font-normal outline-offset-0 hover:bg-background focus-visible:border-ring focus-visible:outline-[3px] focus-visible:outline-ring/20"
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}> 
            {selectedEntity 
              ? (selectedEntity.SchemaName || selectedEntity.LogicalName)
              : placeholder
            }
          </span>
          <ChevronDown
            size={16}
            strokeWidth={2}
            className="shrink-0 text-muted-foreground/80"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-[var(--radix-popper-anchor-width)] border-input p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Type to search entities..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {filteredEntities.length === 0 ? (
              <CommandEmpty>No entities found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredEntities.map((entity) => (
                  <CommandItem
                    key={entity.LogicalName}
                    value={entity.LogicalName}
                    onSelect={() => {
                      onValueChange(entity.LogicalName);
                      setOpen(false);
                      setSearchQuery("");
                    }}
                    className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                  >
                    <div className="flex flex-col w-full">
                      <span className="font-medium text-foreground">{entity.SchemaName || entity.LogicalName}</span>
                      {entity.SchemaName && entity.LogicalName !== entity.SchemaName && (
                        <span className="text-xs text-muted-foreground/80">{entity.LogicalName}</span>
                      )}
                    </div>
                    {value === entity.LogicalName && (
                      <Check size={16} strokeWidth={2} className="ml-auto text-primary" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface FieldData {
  logicalName?: string;
  LogicalName?: string;
  displayName?: string;
  SchemaName?: string;
  description?: string;
  AttributeType?: string;
  type?: string;
}

// Field Search Select Component - Similar to Entity Search Select but for fields
const FieldSearchSelect: React.FC<{
  value: string;
  onValueChange: (value: string) => void;
  fields: FieldData[];
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}> = ({ value, onValueChange, fields, isLoading = false, disabled = false, placeholder = "Select field..." }) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Debug field matching
  React.useEffect(() => {
    if (value && fields.length > 0) {
      // console.log('FieldSearchSelect: Looking for field value:', value);
      // console.log('FieldSearchSelect: Available fields sample:', fields.slice(0, 5).map(f => ({
      //   logicalName: f.logicalName,
      //   LogicalName: f.LogicalName,
      //   displayName: f.displayName,
      //   allProps: Object.keys(f)
      // })));

      // Show custom fields (pl_ prefix) to understand what's available
      // const customFields = fields.filter(f =>
      //   (f.logicalName || f.LogicalName || '').toLowerCase().startsWith('pl_')
      // ).slice(0, 10); // Show first 10 custom fields
      // console.log('FieldSearchSelect: Custom fields (pl_*):', customFields.map(f => ({
      //   name: f.logicalName || f.LogicalName,
      //   display: f.displayName
      // })));

      // Search for the specific field we're looking for
      // const targetField = fields.find(f =>
      //   (f.logicalName || f.LogicalName || '').toLowerCase().includes('estimated_book_price')
      // );
      // console.log('FieldSearchSelect: Target field search for "estimated_book_price":', targetField);

      // Show all fields that contain "price" to see if there are similar fields
      // const priceFields = fields.filter(f =>
      //   (f.logicalName || f.LogicalName || '').toLowerCase().includes('price')
      // );
      // console.log('FieldSearchSelect: Fields containing "price":', priceFields.map(f => f.logicalName || f.LogicalName));

      // const found = fields.find(field => {
      //   const fieldLogicalName = field.logicalName || field.LogicalName;
      //   return fieldLogicalName && fieldLogicalName.toLowerCase() === value.toLowerCase();
      // });
      // console.log('FieldSearchSelect: Found field:', found ? 'YES' : 'NO', found ? { name: found.logicalName || found.LogicalName, displayName: found.displayName } : null);

      // Show warning if field not found
      // if (value && !found && fields.length > 0) {
      //   console.warn(`FieldSearchSelect: Field "${value}" not found in entity. It may have been removed or renamed.`);
      // }
    }
  }, [value, fields]);

  // Improved field matching - case insensitive and handles multiple property formats
  const selectedField = fields.find(field => {
    const fieldLogicalName = field.logicalName || field.LogicalName;
    if (!fieldLogicalName || !value) return false;
    return fieldLogicalName.toLowerCase() === value.toLowerCase();
  });

  // Performance optimization with memoization and capped results
  const filteredFields = React.useMemo(() => {
    if (!searchQuery.trim()) return fields.slice(0, 50); // Limit initial display
    
    const query = searchQuery.toLowerCase();
    const results = fields.filter(field => 
      (field.displayName || '').toLowerCase().includes(query) ||
      (field.SchemaName || '').toLowerCase().includes(query) ||
      (field.LogicalName || '').toLowerCase().includes(query) ||
      (field.logicalName || '').toLowerCase().includes(query) ||
      (field.description || '').toLowerCase().includes(query)
    );
    
    return results.slice(0, 100); // Cap search results for performance
  }, [fields, searchQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(
            "w-full justify-between bg-background px-3 font-normal outline-offset-0 hover:bg-background focus-visible:border-ring focus-visible:outline-[3px] focus-visible:outline-ring/20",
            value && !selectedField && fields.length > 0 && "border-red-300 text-red-600"
          )}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {isLoading ? "Loading fields..." : selectedField
              ? (selectedField.displayName || selectedField.SchemaName || selectedField.LogicalName || selectedField.logicalName)
              : value && fields.length > 0
              ? `${value} (Field not found)`
              : placeholder
            }
          </span>
          <ChevronDown
            size={16}
            strokeWidth={2}
            className="shrink-0 text-muted-foreground/80"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-[var(--radix-popper-anchor-width)] border-input p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Type to search fields..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {/* Show option to clear field if current value is not found */}
            {value && !selectedField && fields.length > 0 && (
              <CommandGroup>
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  Current field "{value}" not found. It may have been removed or renamed.
                </div>
                <CommandItem
                  onSelect={() => {
                    onValueChange('');
                    setOpen(false);
                    setSearchQuery("");
                  }}
                  className="cursor-pointer hover:bg-accent hover:text-accent-foreground text-red-600"
                >
                  🗑️ Clear invalid field
                </CommandItem>
              </CommandGroup>
            )}

            {filteredFields.length === 0 && (!value || selectedField) ? (
              <CommandEmpty>{isLoading ? "Loading..." : "No fields found."}</CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredFields.map((field) => {
                  const fieldValue = field.logicalName || field.LogicalName;
                  const fieldDisplayName = field.displayName || field.SchemaName || field.LogicalName || field.logicalName;
                  const isSelected = fieldValue && value && fieldValue.toLowerCase() === value.toLowerCase();
                  return (
                    <CommandItem
                      key={fieldValue}
                      value={fieldValue}
                      onSelect={() => {
                        if (fieldValue) {
                          onValueChange(fieldValue);
                          setOpen(false);
                          setSearchQuery("");
                        }
                      }}
                      className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                    >
                      <div className="flex flex-col w-full">
                        <span className="font-medium text-foreground">{fieldDisplayName}</span>
                        {fieldDisplayName !== fieldValue && (
                          <span className="text-xs text-muted-foreground/80">{fieldValue}</span>
                        )}
                      </div>
                      {isSelected && (
                        <Check size={16} strokeWidth={2} className="ml-auto text-primary" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const StaticValueCondition: React.FC<StaticValueConditionProps> = ({
  condition,
  entities,
  onChange,
  parentEntity,
  childEntity,
  filterLocation
}) => {
  // All hooks must be called before any early returns
  const [fields, setFields] = React.useState<FieldData[]>([]);
  const [loadingFields, setLoadingFields] = React.useState(false);
  const [optionSetValues, setOptionSetValues] = React.useState<Record<number, string>>({});
  const [loadingOptionSet, setLoadingOptionSet] = React.useState(false);
  const previousEntityRef = React.useRef<string | null>(null);

  // For cross-entity field comparisons (eq_source_field), we need to fetch source entity fields
  const [sourceFields, setSourceFields] = React.useState<FieldData[]>([]);
  const [loadingSourceFields, setLoadingSourceFields] = React.useState(false);
  const isSourceFieldOperator = condition?.operator === 'eq_source_field' || condition?.operator === 'ne_source_field';

  // Get the selected field to determine valid operators - use case insensitive matching
  const selectedField = fields.find(field => {
    const fieldLogicalName = field.logicalName || field.LogicalName;
    if (!fieldLogicalName || !condition?.field) return false;
    return fieldLogicalName.toLowerCase() === condition.field.toLowerCase();
  });

  // Get valid operators for the selected field type
  const validOperators = React.useMemo(() => {
    try {
      // Determine if we should exclude cross-entity operators:
      // - Source filters (Stage 1) never allow cross-entity operators
      // - Target filters (Stage 2) with parent/child context allow cross-entity operators
      // - Manual mapping page (no context) excludes cross-entity operators
      const shouldExcludeCrossEntity =
        filterLocation === 'source' || // Stage 1 - only has source entity
        !parentEntity || // No parent/child context
        !childEntity;    // No parent/child context

      return getOperatorsForFieldType(
        selectedField?.AttributeType || selectedField?.type,
        { excludeCrossEntityOperators: shouldExcludeCrossEntity }
      ) || [];
    } catch (error) {
      console.error('[StaticValueCondition] Error getting operators:', error);
      return [];
    }
  }, [selectedField, filterLocation, parentEntity, childEntity]);

  // Check if the current operator requires a value
  const selectedOperator = validOperators?.find(op =>
    op &&
    typeof op === 'object' &&
    op.value &&
    condition?.operator &&
    op.value === condition.operator
  );
  const requiresValue = selectedOperator?.requiresValue ?? true;

  // Fetch fields when entity changes
  React.useEffect(() => {
    if (!condition?.entity) {
      setFields([]);
      previousEntityRef.current = null;
      return;
    }

    // Prevent infinite loops - if we've already processed this entity, skip
    if (previousEntityRef.current === condition.entity) {
      return;
    }
    
    const loadFields = async () => {
      setLoadingFields(true);
      try {
        console.log('Fetching fields for entity:', condition.entity);
        const fieldData = await fetchEntityFields(condition.entity);
        setFields(Array.isArray(fieldData) ? fieldData : []);
        // console.log('Fetched fields:', fieldData);
        
        // Don't automatically reset field when entity changes to prevent infinite loops
        // The user can manually clear the field if needed
        
        previousEntityRef.current = condition.entity;
      } catch (_error) {
        console.error('Failed to fetch entity fields:', _error);
        setFields([]);
      } finally {
        setLoadingFields(false);
      }
    };
    
    loadFields();
  }, [condition?.entity]); // Only depend on entity changes to prevent infinite loops

  // Fetch source entity fields when operator is eq_source_field or ne_source_field
  React.useEffect(() => {
    if (!isSourceFieldOperator || !parentEntity) {
      setSourceFields([]);
      return;
    }

    const loadSourceFields = async () => {
      setLoadingSourceFields(true);
      try {
        // console.log('Fetching source entity fields for:', parentEntity);
        const fieldData = await fetchEntityFields(parentEntity);
        setSourceFields(Array.isArray(fieldData) ? fieldData : []);
        // console.log('Fetched source fields:', fieldData);
      } catch (error) {
        console.error('Failed to fetch source entity fields:', error);
        setSourceFields([]);
      } finally {
        setLoadingSourceFields(false);
      }
    };

    loadSourceFields();
  }, [isSourceFieldOperator, parentEntity]);

  // Fetch option set values when field changes and it's a picklist field
  React.useEffect(() => {
    if (!condition.entity || !condition.field || !selectedField) {
      setOptionSetValues({});
      return;
    }

    // Check if field is a picklist type (case-insensitive)
    const fieldType = (selectedField.AttributeType || selectedField.type || '').toLowerCase();
    const isPicklistField = fieldType === 'picklist' || fieldType === 'state' || fieldType === 'status';
    
    if (!isPicklistField) {
      setOptionSetValues({});
      return;
    }

    const loadOptionSetValues = async () => {
      setLoadingOptionSet(true);
      try {
        // console.log('Fetching option set values for:', condition.entity, condition.field);
        const optionData = await fetchOptionSetValues(condition.entity, condition.field);
        // console.log('Fetched option set values:', optionData);
        setOptionSetValues(optionData || {});
      } catch (error) {
        console.error('Failed to fetch option set values:', error);
        setOptionSetValues({});
      } finally {
        setLoadingOptionSet(false);
      }
    };

    loadOptionSetValues();
  }, [condition.entity, condition.field, selectedField]);

  const updateCondition = (updates: Partial<StaticCondition>) => {
    console.log('Updating condition with:', updates);
    
    // If we're updating the field, check if the current operator is still valid
    if (updates.field !== undefined) {
      const newField = fields.find(field => {
        const fieldLogicalName = field.logicalName || field.LogicalName;
        return fieldLogicalName && updates.field && fieldLogicalName.toLowerCase() === updates.field.toLowerCase();
      });
      
      if (newField) {
        const shouldExcludeCrossEntity =
          filterLocation === 'source' ||
          !parentEntity ||
          !childEntity;

        const newValidOperators = getOperatorsForFieldType(
          newField.AttributeType || newField.type,
          { excludeCrossEntityOperators: shouldExcludeCrossEntity }
        );
        const currentOperatorValid = newValidOperators.some(op => op.value === condition.operator);
        
        // If current operator is not valid for the new field type, reset it
        if (!currentOperatorValid && condition.operator) {
          console.log('Current operator not valid for field type, resetting operator');
          onChange({ ...condition, ...updates, operator: '' });
          return;
        }
      }
    }
    
    // If we're updating the operator, check if it requires a value
    if (updates.operator !== undefined) {
      const newOperator = validOperators.find(op => op.value === updates.operator);
      const newOperatorRequiresValue = newOperator?.requiresValue ?? true;
      
      // If the new operator doesn't require a value, clear the value field
      if (!newOperatorRequiresValue) {
        console.log('Operator does not require value, clearing value field');
        onChange({ ...condition, ...updates, value: '' });
        return;
      }
    }
    
    onChange({ ...condition, ...updates });
  };

  // Helper function to check if the current field is a picklist
  const isPicklistField = React.useMemo(() => {
    if (!selectedField) return false;
    const fieldType = (selectedField.AttributeType || selectedField.type || '').toLowerCase();
    const result = fieldType === 'picklist' || fieldType === 'state' || fieldType === 'status' || 
           fieldType === 'optionset' || fieldType === 'choice' || fieldType === 'multiselectpicklist';
    return result;
  }, [selectedField]);

  // Component for option set value selection
  const OptionSetSelect: React.FC<{
    value: string;
    onChange: (value: string) => void;
    options: Record<number, string>;
    loading: boolean;
  }> = ({ value, onChange, options, loading }) => {
    const [open, setOpen] = React.useState(false);
    
    const optionEntries = Object.entries(options).map(([key, label]) => ({
      value: key,
      label: label
    }));

    const selectedOption = optionEntries.find(opt => opt.value === value);

    if (loading) {
      return (
        <div className="flex items-center justify-center h-10 px-3 border rounded-md bg-muted">
          <span className="text-sm text-muted-foreground">Loading options...</span>
        </div>
      );
    }

    if (optionEntries.length === 0) {
      return (
        <div className="flex items-center justify-center h-10 px-3 border rounded-md bg-muted">
          <span className="text-sm text-muted-foreground">No options available</span>
        </div>
      );
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-background px-3 font-normal outline-offset-0 hover:bg-background focus-visible:border-ring focus-visible:outline-[3px] focus-visible:outline-ring/20"
          >
            <span className={cn("truncate", !selectedOption && "text-muted-foreground")}>
              {selectedOption ? selectedOption.label : "Select option..."}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search options..." />
            <CommandList>
              <CommandEmpty>No options found.</CommandEmpty>
              <CommandGroup>
                {optionEntries.map(option => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  // Check if we're in parent/child mode
  const hasParentChildContext = parentEntity && childEntity;

  // Auto-set entity based on filterLocation
  React.useEffect(() => {
    if (!filterLocation || !hasParentChildContext) return;

    const targetEntityName = filterLocation === 'source' ? parentEntity : childEntity;
    const targetEntityLabel = filterLocation === 'source' ? 'parent' : 'child';

    // Only update if different from current state
    if (condition?.entity !== targetEntityName) {
      console.log(`[StaticValueCondition] Auto-setting entity based on filterLocation: ${filterLocation} → ${targetEntityName}`);
      updateCondition({
        entity: targetEntityName,
        targetEntity: targetEntityLabel,
        field: '' // Reset field when entity changes
      });
    }
  }, [filterLocation, hasParentChildContext, parentEntity, childEntity, condition?.entity, updateCondition]);

  // Early validation of condition prop - MUST come after all hooks
  if (!condition || typeof condition !== 'object') {
    console.error('[StaticValueCondition] Invalid condition:', condition);
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-600 text-sm">Invalid filter condition. Please remove this filter and create a new one.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-semibold mb-2">Static Value Condition</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 border rounded-lg">
          {/* Hide Target Entity dropdown when filterLocation is provided (auto-managed) */}
          {hasParentChildContext && !filterLocation && (
            <div>
              <Label htmlFor={`target-entity-${condition.id}`}>Target Entity</Label>
              <Select
                value={condition.targetEntity || ''}
                onValueChange={(value: 'parent' | 'child') => {
                  const entityName = value === 'parent' ? parentEntity : childEntity;
                  updateCondition({
                    targetEntity: value,
                    entity: entityName,
                    field: ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select target entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent">
                    Parent Entity ({parentEntity})
                  </SelectItem>
                  <SelectItem value="child">
                    Child Entity ({childEntity})
                  </SelectItem>
                </SelectContent>
              </Select>
              {condition.targetEntity && (
                <div className="text-xs text-muted-foreground mt-1">
                  {condition.targetEntity === 'parent'
                    ? `Filters records being processed from ${parentEntity}`
                    : `Filters records being updated in ${childEntity}`
                  }
                </div>
              )}
            </div>
          )}

          <div>
            <Label htmlFor={`entity-${condition.id}`}>
              {filterLocation
                ? `Entity (${filterLocation === 'source' ? 'Source' : 'Target'} - Auto-selected)`
                : hasParentChildContext ? 'Entity (Auto-selected)' : 'Entity'
              }
            </Label>
            {filterLocation ? (
              // Show readonly display when filterLocation is set
              <div className="flex items-center h-10 px-3 border rounded-md bg-muted text-sm">
                <span className="font-medium">
                  {condition.entity || (filterLocation === 'source' ? parentEntity : childEntity)}
                </span>
                <span className="ml-2 text-xs text-muted-foreground">
                  ({filterLocation === 'source' ? 'Parent' : 'Child'})
                </span>
              </div>
            ) : (
              <EntitySearchSelect
                value={condition.entity}
                onValueChange={(value) => updateCondition({ entity: value, field: '' })}
                entities={entities}
                placeholder="Search for entity..."
              />
            )}
          </div>

          <div>
            <Label htmlFor={`field-${condition.id}`}>
              Field
              {condition.field && !selectedField && fields.length > 0 && (
                <span className="ml-2 text-xs text-red-500 font-normal">⚠️ Field not found</span>
              )}
            </Label>
            <FieldSearchSelect
              value={condition.field}
              onValueChange={(value) => {
    const selected = fields.find(field => {
      const fieldLogicalName = field.logicalName || field.LogicalName;
      return fieldLogicalName && fieldLogicalName.toLowerCase() === value.toLowerCase();
    });
    if (selected) {
      const fieldType = (selected.AttributeType || selected.type || '').toLowerCase();
      const isPicklist = fieldType === 'picklist' || fieldType === 'state' || fieldType === 'status' || 
                         fieldType === 'optionset' || fieldType === 'choice' || fieldType === 'multiselectpicklist';
      console.log('Field selected:', {
        fieldName: value,
        field: selected,
        fieldType: selected.AttributeType,
        isPicklist: isPicklist
      });
    }
    updateCondition({ field: value });
  }}
              fields={fields}
              isLoading={loadingFields}
              disabled={!condition.entity || loadingFields}
              placeholder="Search for field..."
            />
          </div>

          <div>
            <Label htmlFor={`operator-${condition.id}`}>Operator</Label>
            <Select
              value={condition.operator}
              onValueChange={(value) => updateCondition({ operator: value })}
              disabled={!condition.field}
            >
              <SelectTrigger>
                <SelectValue placeholder={!condition.field ? "Select field first" : "Select operator"} />
              </SelectTrigger>
              <SelectContent>
                {validOperators.map(op => (
                  <SelectItem key={op.value} value={op.value}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedField && (
              <div className="text-xs text-muted-foreground mt-1">
                Field type: {selectedField.AttributeType || selectedField.type || 'unknown'}
              </div>
            )}
          </div>

          {requiresValue && (
            <div>
              <Label htmlFor={`value-${condition.id}`}>
                {condition.operator === 'eq_field' || condition.operator === 'ne_field'
                  ? 'Compare to Field'
                  : condition.operator === 'eq_source_field' || condition.operator === 'ne_source_field'
                  ? 'Compare to Source Field'
                  : 'Value'}
              </Label>
              {condition.operator === 'eq_field' || condition.operator === 'ne_field' ? (
                // For same-entity field comparison operators, show ONLY compatible fields
                (() => {
                  // Filter to only compatible field types
                  const compatibleFields = selectedField
                    ? filterCompatibleFields(
                        {
                          attributeType: selectedField.AttributeType || selectedField.type,
                          logicalName: selectedField.logicalName || selectedField.LogicalName || '',
                          displayName: selectedField.displayName
                        },
                        fields.map(f => ({
                          attributeType: f.AttributeType || f.type,
                          logicalName: f.logicalName || f.LogicalName || '',
                          displayName: f.displayName,
                          ...f // Preserve all other properties
                        }))
                      )
                    : fields;

                  // Check if current value is incompatible
                  const selectedComparisonField = fields.find(f =>
                    (f.logicalName || f.LogicalName) === condition.value
                  );
                  const isCurrentValueIncompatible =
                    condition.value &&
                    selectedField &&
                    selectedComparisonField &&
                    !areFieldTypesCompatible(
                      selectedField.AttributeType || selectedField.type,
                      selectedComparisonField.AttributeType || selectedComparisonField.type
                    );

                  return (
                    <>
                      <FieldSearchSelect
                        value={condition.value?.toString() || ''}
                        onValueChange={(value) => updateCondition({ value })}
                        fields={compatibleFields}
                        isLoading={loadingFields}
                        disabled={!condition.entity || loadingFields || !selectedField}
                        placeholder={selectedField ? "Select field to compare..." : "Select a field first"}
                      />
                      {selectedField && (
                        <div className="text-xs text-muted-foreground mt-1">
                          <span className="font-medium">Compatible types:</span> {getShortTypeDisplayName(selectedField.AttributeType || selectedField.type)}
                        </div>
                      )}
                      {isCurrentValueIncompatible && (
                        <div className="text-xs text-red-600 mt-1 flex items-start gap-1 p-2 bg-red-50 border border-red-200 rounded">
                          <span className="font-medium">⚠️</span>
                          <span>
                            {getIncompatibilityReason(
                              {
                                attributeType: selectedField.AttributeType || selectedField.type || '',
                                logicalName: condition.field,
                                displayName: selectedField.displayName
                              },
                              {
                                attributeType: selectedComparisonField.AttributeType || selectedComparisonField.type || '',
                                logicalName: selectedComparisonField.logicalName || selectedComparisonField.LogicalName || '',
                                displayName: selectedComparisonField.displayName
                              }
                            )}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()
              ) : condition.operator === 'eq_source_field' || condition.operator === 'ne_source_field' ? (
                // For cross-entity field comparison operators, show ONLY compatible source fields
                (() => {
                  // Filter to only compatible field types from source entity
                  const compatibleSourceFields = selectedField
                    ? filterCompatibleFields(
                        {
                          attributeType: selectedField.AttributeType || selectedField.type,
                          logicalName: selectedField.logicalName || selectedField.LogicalName || '',
                          displayName: selectedField.displayName
                        },
                        sourceFields.map(f => ({
                          attributeType: f.AttributeType || f.type,
                          logicalName: f.logicalName || f.LogicalName || '',
                          displayName: f.displayName,
                          ...f // Preserve all other properties
                        }))
                      )
                    : sourceFields;

                  // Check if current value is incompatible
                  const selectedSourceField = sourceFields.find(f =>
                    (f.logicalName || f.LogicalName) === condition.value
                  );
                  const isCurrentValueIncompatible =
                    condition.value &&
                    selectedField &&
                    selectedSourceField &&
                    !areFieldTypesCompatible(
                      selectedField.AttributeType || selectedField.type,
                      selectedSourceField.AttributeType || selectedSourceField.type
                    );

                  return (
                    <>
                      <FieldSearchSelect
                        value={condition.value?.toString() || ''}
                        onValueChange={(value) => updateCondition({ value })}
                        fields={compatibleSourceFields}
                        isLoading={loadingSourceFields}
                        disabled={!parentEntity || loadingSourceFields || !selectedField}
                        placeholder={
                          !parentEntity
                            ? "Source entity not available"
                            : !selectedField
                            ? "Select a field first"
                            : "Select source field to compare..."
                        }
                      />
                      {selectedField && parentEntity && (
                        <div className="text-xs text-muted-foreground mt-1">
                          <span className="font-medium">Compatible types:</span> {getShortTypeDisplayName(selectedField.AttributeType || selectedField.type)} (from source entity)
                        </div>
                      )}
                      {isCurrentValueIncompatible && (
                        <div className="text-xs text-red-600 mt-1 flex items-start gap-1 p-2 bg-red-50 border border-red-200 rounded">
                          <span className="font-medium">⚠️</span>
                          <span>
                            {getIncompatibilityReason(
                              {
                                attributeType: selectedField.AttributeType || selectedField.type || '',
                                logicalName: condition.field,
                                displayName: selectedField.displayName
                              },
                              {
                                attributeType: selectedSourceField.AttributeType || selectedSourceField.type || '',
                                logicalName: selectedSourceField.logicalName || selectedSourceField.LogicalName || '',
                                displayName: selectedSourceField.displayName
                              }
                            )}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()
              ) : isPicklistField ? (
                <OptionSetSelect
                  value={condition.value?.toString() || ''}
                  onChange={(value) => updateCondition({ value })}
                  options={optionSetValues}
                  loading={loadingOptionSet}
                />
              ) : (
                <Input
                  id={`value-${condition.id}`}
                  value={condition.value?.toString() || ''}
                  onChange={(e) => updateCondition({ value: e.target.value })}
                  placeholder="Enter value"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
